import React, { useState } from 'react';
import { Dropdown } from 'design-system-web';
import { useCategoryListQuery } from 'pages/portal/admin/channel/products-services/helpers';
import { ProductCategory, ProductType } from 'graphql-query-contracts';
import { Item } from 'components/form/Dropdown/Dropdown';
import { CreateCategoryFormModal } from 'pages/portal/admin/channel/products-services/products/modals/CreateCategoryFormModal';
import { useTranslation } from 'react-i18next';
import styles from './ProductCategoryDropdown.scss';

export function ProductCategoryDropdown({
  productType = ProductType.ProductTypeUnknown,
  value,
  handleCategorySelection,
  disabled = false,
  label = '',
  placeholder = '',
  fixedLabel = false,
  className,
  isRequired = false,
  isFullWidth = false,
  isCreateCategoryEnabled = false,
}: {
  value: any;
  handleCategorySelection: Function;
  productType?: ProductType;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  fixedLabel?: boolean;
  className?: string | string[];
  isRequired?: boolean;
  isFullWidth?: boolean;
  isCreateCategoryEnabled?: boolean;
}) {
  const { data: categoryList, refetch } = useCategoryListQuery({
    page: 0,
    pageSize: 100,
    productType,
  });

  const { t } = useTranslation();
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  function getCategoryItems(): Item<string>[] | undefined {
    const listOfCategories = categoryList?.listProductCategories
      .productCategory as ProductCategory[] | undefined;

    if (listOfCategories) {
      const dropdownCategoryItems = listOfCategories.map(productCategory => {
        return {
          label: productCategory.name,
          value: productCategory.productCategoryId,
        };
      });

      return dropdownCategoryItems;
    }

    return undefined;
  }

  const onCategorySelection = (item: Item<string>) => {
    const listOfCategories = categoryList?.listProductCategories
      .productCategory as ProductCategory[];
    const selectedCategory = listOfCategories.find(
      productCategory => productCategory.productCategoryId === item.value
    );

    if (selectedCategory) {
      handleCategorySelection(selectedCategory);
    }
  };

  const categoryLabel = (labelText: string, required = false) => {
    const requiredFieldAsteriskSymbol = '*';

    return (
      <label className={styles.descriptionLabel}>
        {t(labelText)}
        {required && (
          <span className={styles.InputLabelRequiredAstrisk}>
            {requiredFieldAsteriskSymbol}
          </span>
        )}
      </label>
    );
  };

  return (
    <>
      {isCategoryModalOpen && (
        <CreateCategoryFormModal
          modalVisible={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          onSave={() => refetch()}
          testId="createCategoryFormModal"
        />
      )}
      <div>
        {isCreateCategoryEnabled && (
          <div className={styles.label}>
            {categoryLabel(
              'abp.productsServices.createProduct.categoryLabel',
              true
            )}
            <button
              className={styles.modalButton}
              onClick={() => setIsCategoryModalOpen(true)}
            >
              {t('abp.productsServices.categories.button')}
            </button>
          </div>
        )}
        <Dropdown
          testId="categoryDropdown"
          doTranslation={false}
          value={value}
          items={getCategoryItems()}
          onChange={onCategorySelection}
          label={label}
          placeholder={placeholder}
          fixedLabel={fixedLabel}
          disabled={disabled}
          className={className}
          isRequired={isRequired}
          isFullWidth={isFullWidth}
        />
      </div>
    </>
  );
}
